import React, { Component } from 'react'

import { Bell, ChevronDown, Share2, type LucideIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { OperationType } from '~/client/graph'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'

import { NotificationSetupModes } from '../../../../NotificationsSetup'
import ActionPickerStore from './ActionPicker.store'

interface IActionPickerProps {
  operationType: OperationType
  operationRuleId: string
  handleRuleTypeChange: (operationType: OperationType) => void
  graphExecutorStore?: GraphExecutorStore
  projectId: string
  mode: NotificationSetupModes
}

interface IActionConfig {
  Icon: LucideIcon
  text: string
}

@inject('graphExecutorStore')
@observer
export default class ActionPicker extends Component<IActionPickerProps> {
  private readonly store: ActionPickerStore

  public constructor(props: IActionPickerProps) {
    super(props)
    this.store = new ActionPickerStore(
      props.graphExecutorStore,
      props.projectId,
    )
  }

  private get actionConfig(): IActionConfig {
    return this.props.operationType === OperationType.Notification
      ? {
          // A bell, not an envelope: a rule drives every channel, not just email.
          Icon: Bell,
          text: Localization.translator.sendNotification,
        }
      : {
          Icon: Share2,
          text: Localization.translator.toExternalCalendar,
        }
  }

  public componentDidMount() {
    this.store.fetchData()
  }

  public render() {
    const { Icon: ActionIcon, text: actionText } = this.actionConfig
    const isExternalCalendarDisabled =
      this.props.mode === NotificationSetupModes.form
        ? this.store.isFormsExternalCalendarDisabled
        : this.store.isDeliveriesExternalCalendarDisabled

    return (
      <div className="mw270 tw:flex tw:items-center px20 py15 brada4 bg-transparent">
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant="outline"
                className="tw:w-full tw:justify-between"
              />
            }
          >
            <ActionIcon size={16} data-icon="inline-start" />
            <span className="tw:flex-1 tw:text-left tw:truncate">
              {actionText}
            </span>
            <ChevronDown size={16} data-icon="inline-end" />
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" align="center">
            <DropdownMenuItem
              onClick={this.props.handleRuleTypeChange.bind(
                null,
                OperationType.Notification,
              )}
            >
              <Bell />
              {Localization.translator.sendNotification}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <Tooltip disabled={!isExternalCalendarDisabled}>
              <TooltipTrigger render={<div />}>
                <DropdownMenuItem
                  disabled={isExternalCalendarDisabled}
                  onClick={this.props.handleRuleTypeChange.bind(
                    null,
                    OperationType.ExternalCalendar,
                  )}
                >
                  <Share2 />
                  {Localization.translator.toExternalCalendar}
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent>
                {this.props.mode === NotificationSetupModes.form
                  ? Localization.translator.allFormsSharingCalendarEnabled
                  : Localization.translator.allDeliveriesSharingCalendarEnabled}
              </TooltipContent>
            </Tooltip>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    )
  }
}
